import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import * as Sentry from '@sentry/react';

import { useProject } from '@contexts/Project.context';
import { useToast } from '@contexts/Toast.context';
import { useAnnotationSessionStore } from '@stores/useAnnotationSessionStore';
import useKeyboardShortcut from '@hooks/useKeyboardShortcut.hook';
import { postNodeAnnotations, putTileAnnotations } from '@utils/api/annotations';
import { invalidateCollectionResults } from '@utils/collectionQueries';
import { ANNOTATION_BOX_FILTER } from '@utils/constants';
import {
	activeSquareRef,
	annotationSource,
	notifyBoxSelected,
	selectBox,
	setAnnotationLabel,
	setBoxFilter,
	subscribeBoxSelection,
} from '@utils/map/annotation/annotation.interaction';
import { clearHistory, undoLast } from '@utils/map/annotation/boxHistory';
import { isInsideModal } from '@utils/map/annotation/modalDialog';
import { setObjectDrawingActive } from '@utils/map/object.draw';

import {
	boxesFromFeatures,
	boxesOf,
	hasChanges,
	restoreBoxes,
	takeSnapshot,
	tileHasRows,
	tileSavePayload,
	tileReadRing,
} from './annotationSession';

const SOURCE_EVENTS = ['addfeature', 'removefeature', 'changefeature'];

/**
 * The session of object annotation inside an annotator: which object the live
 * boxes belong to, whether the open tile differs from what the backend stored,
 * loading an object's list onto the map, saving a tile, and switching the
 * session to the object the panel picked.
 *
 * Every box of every object stays on the map for the whole session, saved or
 * not; the styles mute the ones of other objects. The list (`loadNode`) puts
 * the model boxes and the annotators' rows of an object on the map; the tile
 * save (`saveTile`) sends the open tile's adds, edits and rejects, then reads
 * the tile back so the map shows what was stored. A failed call toasts and
 * keeps the local state.
 *
 * The annotator calls `track()` once its draw source is registered (see
 * `setAnnotationSource`), and names the open tile through `tileName`. A tile
 * counts as changed when a box in it is new or moved, a saved box is gone, or
 * a model box was erased; a tile never saved can also be saved (as an empty mark).
 *
 * @param {object} options
 * @param {boolean} options.enabled - false in a Model view session
 * @param {object|null} options.activeNode - the object the panel marks active
 * @param {string|undefined} options.tileName - the open tile or image, undefined while none is open
 * @param {(node: object) => Promise<Array|null>} options.loadTilesFor - the tiles
 *   of another object inside the session area; `[]` means none, `null` keeps the current tiles
 * @param {(result: { node: object, tiles: Array|null }) => void} [options.onSwitched]
 * @param {{ projection: '3857'|'normalized', toRing: (ring: number[][]) => number[][],
 *   toCoords: (rings: number[][][], tileName: string) => number[][][] }} [options.serialize] -
 *   how boxes travel: the projection of both calls, a map ring to request ring, and the
 *   list's GeoJSON coordinates to map coordinates. Without it the session stays local.
 * @param {number[][][]} [options.boundary] - the drawn area (GeoJSON Polygon coordinates);
 *   its ring limits the list to the session area
 * @param {(tileName: string) => number[][]|undefined} [options.tileAoi] - the ring of a
 *   tile, to read just that tile back after a save
 * @param {(box: import('ol/Feature').default, preferred?: string) => string|undefined}
 *   [options.tileOfBox] - the session tile a box lies in (by its center), `preferred`
 *   when that one holds it; model boxes are filed under it
 * @param {boolean} [options.perTile] - the map shows one tile at a time (single images)
 * @returns {{ canSave: boolean, savesEmpty: boolean, hasUnsavedBoxes: boolean, saving: boolean, loading: boolean,
 *   switching: boolean, saveTile: () => Promise<boolean>, loadNode: (node: object) => Promise<void>,
 *   restoreTile: (tileName: string) => void,
 *   hasUnsavedChanges: (options?: { activeOnly?: boolean }) => boolean,
 *   canSaveOpenTile: () => boolean, isOpenTileReading: () => boolean,
 *   discardTile: () => void, track: () => void }}
 */
export const useObjectAnnotationSession = ({
	enabled,
	activeNode,
	tileName,
	loadTilesFor,
	onSwitched,
	serialize,
	boundary,
	tileAoi,
	tileOfBox,
	perTile = false,
}) => {
	const { project, dispatch } = useProject();
	const { addToast } = useToast();
	const queryClient = useQueryClient();
	// The store slices that re-render the annotator: the unsaved flag here, the
	// sticky label and the box filter below. Everything else is read on demand.
	const dirty = useAnnotationSessionStore(s => s.dirty);
	const store = useAnnotationSessionStore.getState;
	/** A failed list read or save: the toast tells the user, Sentry tells us. */
	const report = (error, action, extra = {}) =>
		Sentry.captureException(error, {
			tags: { feature: 'object-annotation', action },
			extra: { projectUuid: optionsRef.current.project?.uuid, ...extra },
		});

	// The object the live boxes belong to. The panel's pick runs ahead of it
	// while a switch is in flight.
	const sessionNodeRef = useRef(null);
	const [switching, setSwitching] = useState(false);
	const switchingRef = useRef(false);
	switchingRef.current = switching;
	const [loading, setLoading] = useState(false);
	const [saving, setSaving] = useState(false);
	const [savesEmpty, setSavesEmpty] = useState(false);
	// `hasUnsavedChanges()` as state: `canSave` is also true for a never-saved, untouched
	// tile, so only this tells the toolbar to make Save the main action.
	const [hasUnsavedBoxes, setHasUnsavedBoxes] = useState(false);
	// The live source once the annotator has registered it; the listeners hang
	// off an effect keyed on it, so they survive React's development re-mount.
	const [trackedSource, setTrackedSource] = useState(null);

	// The listeners outlive the render that attached them, so they read the open
	// tile through a ref rather than the closure.
	const tileNameRef = useRef(tileName);
	tileNameRef.current = tileName;
	const activeNodeRef = useRef(activeNode);
	activeNodeRef.current = activeNode;
	const optionsRef = useRef({});
	optionsRef.current = { serialize, boundary, tileAoi, tileOfBox, perTile, project, onSwitched };

	// True while the session itself replaces boxes, so those removals are not the user's erasing.
	const bulkRef = useRef(false);
	const bulkDepthRef = useRef(0);
	// A source event came in during a bulk replacement; the recount runs once at its end.
	const refreshPendingRef = useRef(false);
	// Pristine model boxes per object and tile: server state the map and a single
	// image's per-image rebuild draw from. `{ [nodeUuid]: { [tileName]: Feature[] } }`.
	const modelBoxesRef = useRef({});
	// Objects whose list is on the map already.
	const loadedRef = useRef(new Set());
	// Objects whose list is being fetched, so a retry does not run twice.
	const loadingRef = useRef(new Set());
	// Rejects saved before, per object and tile: `{ [nodeUuid]: { [tileName]: row[] } }`.
	// They draw nothing but must travel with every later save of their tile.
	const storedRejectsRef = useRef({});
	// The latest object switch; an earlier one that finishes late is ignored.
	const switchTokenRef = useRef(0);

	/** Rows of the open tile depend on the server's list: no list, no save. */
	const isListed = nodeUuid => !optionsRef.current.serialize || loadedRef.current.has(nodeUuid);
	const storedRejectsIn = (nodeUuid, tile) => storedRejectsRef.current[nodeUuid]?.[tile] ?? [];

	// Tiles whose rows were read around the tile itself, per object
	// (`${nodeUuid}|${tile}`). The first list is cut to the drawn area, which a tile
	// at its edge reaches past; a save replaces every row of the tile, so it waits
	// for this read (orthophoto, where tiles have a square to ask with).
	const freshTilesRef = useRef(new Set());
	const tileKey = (nodeUuid, tile) => `${nodeUuid}|${tile}`;
	// Tiles saved whose read-back failed: the server's rows are unknown, so the tile
	// is read again (and redrawn from the server) before it can be saved again.
	const staleTilesRef = useRef(new Set());
	const isTileRead = (nodeUuid, tile) => {
		const key = tileKey(nodeUuid, tile);
		if (!optionsRef.current.serialize || freshTilesRef.current.has(key)) return true;
		return !optionsRef.current.tileAoi && !staleTilesRef.current.has(key);
	};
	/** What Save needs: the object's list and the tile's own read are both in. */
	const canSaveTile = (nodeUuid, tile) => isListed(nodeUuid) && isTileRead(nodeUuid, tile);

	/**
	 * The one rule for turning drawing back on: a tile (or image) is open and the
	 * session's picture of it for the active object is current. A stale tile is
	 * redrawn from the server, so anything drawn on it would be replaced.
	 */
	const syncDrawing = () => {
		const nodeUuid = sessionNodeRef.current;
		const tile = tileNameRef.current;
		const stale = Boolean(
			nodeUuid && tile && staleTilesRef.current.has(tileKey(nodeUuid, tile))
		);
		setObjectDrawingActive(Boolean(activeSquareRef) && !stale);
	};

	const source = () => annotationSource;

	const refreshDirty = () => {
		const live = source();
		const nodeUuid = sessionNodeRef.current;
		const tile = tileNameRef.current;
		const isOpen = Boolean(live && nodeUuid && tile);
		if (isOpen && !canSaveTile(nodeUuid, tile)) {
			store().setDirty(false);
			store().setEdited(false);
			setSavesEmpty(false);
			store().bumpBoxes();
			setHasUnsavedBoxes(false);
			return;
		}
		const rejects = isOpen ? store().rejectsInTile(nodeUuid, tile) : [];
		const changed =
			isOpen &&
			hasChanges(live, nodeUuid, tile, store().savedInTile(nodeUuid, tile), rejects);
		// A tile never saved can be saved too: as an empty mark when it has no rows.
		const neverSaved = isOpen && store().savedByNode[nodeUuid]?.[tile] === undefined;
		store().setDirty(Boolean(changed || neverSaved));
		store().setEdited(Boolean(changed));
		setHasUnsavedBoxes(Boolean(changed));
		setSavesEmpty(
			isOpen &&
				!tileHasRows(
					boxesOf(live, nodeUuid, tile),
					rejects,
					storedRejectsIn(nodeUuid, tile)
				)
		);
		// The labels card reads the live boxes: tell it they may have changed.
		store().bumpBoxes();
	};

	// Each added, removed or changed box re-checks Save and the counts; while the
	// session replaces many boxes at once (a list, a tile read, Cancel) that waits
	// for the end of the replacement, or the work grows with the square of the boxes.
	const onSourceChange = () => {
		if (bulkRef.current) {
			refreshPendingRef.current = true;
			return;
		}
		refreshDirty();
	};

	/** Watch the live source for unsaved boxes from here on. */
	const track = () => setTrackedSource(source());

	// A model box the user erased is a correction the tile's next save sends.
	const recordErasedModelBox = event => {
		const box = event.feature;
		const nodeUuid = sessionNodeRef.current;
		// A correction (a human edit row) hides the model box it corrects; erasing it
		// rejects that model box too, as erasing a model box does.
		const isCorrection = box.get('source') !== 'model' && Boolean(box.get('targetGeometry'));
		if (bulkRef.current || !nodeUuid || (box.get('source') !== 'model' && !isCorrection))
			return;
		if (box.get('nodeUuid') !== nodeUuid || !box.get('tileName')) return;
		const known = store().rejectsInTile(nodeUuid, box.get('tileName'));
		if (known.some(other => other.get('boxId') === box.get('boxId'))) return;
		store().addReject(nodeUuid, box.get('tileName'), box);
	};

	useEffect(() => {
		if (!trackedSource) return undefined;
		trackedSource.on('removefeature', recordErasedModelBox);
		SOURCE_EVENTS.forEach(event => trackedSource.on(event, onSourceChange));
		refreshDirty();
		return () => {
			trackedSource.un('removefeature', recordErasedModelBox);
			SOURCE_EVENTS.forEach(event => trackedSource.un(event, onSourceChange));
		};
		// refreshDirty reads refs and the store at call time.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [trackedSource]);

	// New boxes carry the active object's sticky label.
	const stickyLabel = useAnnotationSessionStore(
		s => (activeNode ? s.stickyLabelByNode[activeNode.uuid] : null) ?? null
	);
	useEffect(() => {
		if (!enabled) return undefined;
		setAnnotationLabel(stickyLabel);
		return () => setAnnotationLabel(null);
	}, [enabled, stickyLabel]);

	// The panel's box filter decides which boxes the map draws.
	const boxFilter = useAnnotationSessionStore(s => s.boxFilter);
	useEffect(() => {
		if (!enabled) return undefined;
		setBoxFilter(boxFilter);
		return () => setBoxFilter(null);
	}, [enabled, boxFilter]);

	// The box being moved or reshaped, for the label chips.
	useEffect(() => {
		if (!enabled) return undefined;
		const unsubscribe = subscribeBoxSelection(boxId => store().setSelectedBox(boxId));
		return () => {
			unsubscribe();
			notifyBoxSelected(null);
			clearHistory();
			store().setSelectedBox(null);
		};
		// store() is the stable getState.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [enabled]);

	// Ctrl/Cmd+Z reverts the last box change of the open tile, but not from inside a
	// modal (Train New Object, a confirm): the boxes it would change are hidden under it.
	// The object-tree drawer is no modal: undo works after relabelling from its chips.
	useKeyboardShortcut(
		'z',
		enabled && !saving,
		event => {
			if (!isInsideModal(event.target)) undoLast();
		},
		{ mod: true }
	);

	// Another tile or object: its boxes are not the selection's, and an undo must not reach back.
	useEffect(() => {
		if (!enabled) return;
		selectBox(null);
		clearHistory();
	}, [enabled, tileName, activeNode?.uuid]);

	// Opening or closing a tile changes what Save would act on; an opened tile
	// first claims the model boxes it holds (tiles can overlap).
	useEffect(() => {
		if (tileName) claimModelBoxes(tileName);
		// An object whose list failed is retried when a tile opens.
		const node = activeNodeRef.current;
		if (tileName && node && sessionNodeRef.current === node.uuid && !isListed(node.uuid)) {
			loadNode(node);
		}
		if (tileName) readTile(tileName);
		refreshDirty();
		// claimModelBoxes and refreshDirty read refs and the store at call time.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tileName]);

	/** Run a replacement of boxes the user did not make (see `bulkRef`). */
	const bulk = work => {
		bulkDepthRef.current += 1;
		bulkRef.current = true;
		try {
			work();
		} finally {
			bulkDepthRef.current -= 1;
			if (bulkDepthRef.current === 0) {
				bulkRef.current = false;
				// One recount for the whole replacement instead of one per box.
				if (refreshPendingRef.current) {
					refreshPendingRef.current = false;
					refreshDirty();
				}
			}
		}
	};

	/**
	 * Redraw an object from what the session holds for it: its stored human rows
	 * and its pristine model boxes, in one tile or (undefined) in every tile.
	 */
	const showNode = (nodeUuid, tile) => {
		const live = source();
		if (!live) return;
		const models = modelBoxesRef.current[nodeUuid] ?? {};
		const humans =
			tile === undefined ? store().savedFor(nodeUuid) : store().savedInTile(nodeUuid, tile);
		const modelBoxes = tile === undefined ? Object.values(models).flat() : (models[tile] ?? []);
		bulk(() => {
			boxesOf(live, nodeUuid, tile).forEach(box => live.removeFeature(box));
			restoreBoxes(live, [...humans, ...modelBoxes]);
		});
	};

	/**
	 * A model box carries the `tile_name` of the grid its model ran on, which need
	 * not be (or name) a tile of this session. It is filed under the session tile
	 * that holds it (the open one when that holds it: tiles can overlap), so
	 * editing or rejecting it counts in the tile it shows in.
	 */
	const placeModelBoxes = (boxes, preferred = tileNameRef.current) => {
		const { tileOfBox: locate } = optionsRef.current;
		if (!locate) return boxes;
		boxes.forEach(box => {
			if (box.get('source') !== 'model') return;
			const tile = locate(box, preferred);
			if (tile) box.set('tileName', tile, true);
		});
		return boxes;
	};

	/**
	 * The opened tile takes over the model boxes of the session object it holds
	 * that are filed under another (overlapping) tile: the pristine copies move
	 * bucket, and the live boxes follow, so a move or an erase counts here.
	 */
	const claimModelBoxes = tile => {
		const { tileOfBox: locate } = optionsRef.current;
		const nodeUuid = sessionNodeRef.current;
		const live = source();
		if (!locate || !nodeUuid || !live) return;
		const byTile = modelBoxesRef.current[nodeUuid];
		if (!byTile) return;

		const claimed = [];
		Object.entries(byTile).forEach(([bucket, boxes]) => {
			if (bucket === tile) return;
			const taken = boxes.filter(box => locate(box, tile) === tile);
			if (!taken.length) return;
			byTile[bucket] = boxes.filter(box => !taken.includes(box));
			claimed.push(...taken);
		});
		if (!claimed.length) return;
		byTile[tile] = [...(byTile[tile] ?? []), ...claimed];

		const ids = new Set(claimed.map(box => box.get('boxId')));
		claimed.forEach(box => box.set('tileName', tile, true));
		bulk(() => {
			live.getFeatures().forEach(box => {
				if (ids.has(box.get('boxId'))) box.set('tileName', tile, true);
			});
		});
	};

	/** The server's rows of an object on one tile become the session's state for it. */
	const adoptTile = (nodeUuid, tile, collection, node) => {
		const { boxes: listed, rejected } = boxesFromFeatures(collection, {
			nodeUuid,
			color: node?.color,
			toCoords: optionsRef.current.serialize?.toCoords,
		});
		const boxes = placeModelBoxes(listed);
		const inTile = boxes.filter(box => box.get('tileName') === tile);
		store().saveTile(
			nodeUuid,
			tile,
			inTile.filter(box => box.get('source') !== 'model')
		);
		modelBoxesRef.current[nodeUuid] = {
			...modelBoxesRef.current[nodeUuid],
			[tile]: inTile.filter(box => box.get('source') === 'model'),
		};
		storedRejectsRef.current[nodeUuid] = {
			...storedRejectsRef.current[nodeUuid],
			[tile]: rejected.filter(row => row.tileName === tile),
		};
		// Read with the tile's own square: the session's picture of it is complete.
		freshTilesRef.current.add(tileKey(nodeUuid, tile));
		// A single image shows one image: the read of one the user already left updates
		// the session only, and its boxes are drawn when it is opened again.
		if (!optionsRef.current.perTile || tileNameRef.current === tile) showNode(nodeUuid, tile);
	};

	/**
	 * Read the open tile's rows again around the tile (`tileReadRing`: rows match by
	 * their center, so a box filed under the tile but hanging over its edge needs a
	 * wider area), keep the tile's own by `tile_name`, and merge them in:
	 * rows the first (area-wide) list missed are added to the map and the store,
	 * saved boxes the server no longer has are taken off, and the tile's stored
	 * rejects are replaced. Unsaved boxes stay. Until this read is in, Save is off
	 * for the tile; if it fails, Save stays off and opening the tile retries.
	 */
	const readTile = async tile => {
		const { serialize: wire, tileAoi: aoiOf } = optionsRef.current;
		const nodeUuid = sessionNodeRef.current;
		const live = source();
		const ring = tileReadRing(aoiOf?.(tile));
		if (!wire || !nodeUuid || !live || !loadedRef.current.has(nodeUuid)) return;
		const key = tileKey(nodeUuid, tile);
		const stale = staleTilesRef.current.has(key);
		// Without a square (a single image) only a stale tile is read: the whole list,
		// kept to the image by `tile_name`.
		if (!ring && !stale) return;
		if (freshTilesRef.current.has(key) || loadingRef.current.has(key)) return;
		loadingRef.current.add(key);
		// A stale tile is redrawn from the server: nothing may be drawn meanwhile.
		if (stale) setObjectDrawingActive(false);
		try {
			const collection = await requestList(nodeUuid, ring ? { aoi: ring } : {});
			const node = activeNodeRef.current?.uuid === nodeUuid ? activeNodeRef.current : null;
			if (stale) {
				// The local picture is unknown after a failed read-back: the server's wins.
				adoptTile(nodeUuid, tile, collection, node);
				staleTilesRef.current.delete(key);
				return;
			}
			const {
				boxes: listed,
				emptyTiles,
				rejected,
			} = boxesFromFeatures(collection, {
				nodeUuid,
				color: node?.color,
				toCoords: wire.toCoords,
			});
			const inTile = placeModelBoxes(listed, tile).filter(
				box => box.get('tileName') === tile
			);
			const humans = inTile.filter(box => box.get('source') !== 'model');
			const models = inTile.filter(box => box.get('source') === 'model');

			// What the server holds for the tile is what the session holds as saved.
			const known = store().savedByNode[nodeUuid]?.[tile] !== undefined;
			if (known || humans.length || emptyTiles.includes(tile)) {
				store().saveTile(nodeUuid, tile, humans);
			}
			const byTile = modelBoxesRef.current[nodeUuid] ?? {};
			const knownModels = new Set(
				Object.values(byTile)
					.flat()
					.map(box => box.get('boxId'))
			);
			byTile[tile] = [
				...(byTile[tile] ?? []),
				...models.filter(box => !knownModels.has(box.get('boxId'))),
			];
			modelBoxesRef.current[nodeUuid] = byTile;
			storedRejectsRef.current[nodeUuid] = {
				...storedRejectsRef.current[nodeUuid],
				[tile]: rejected.filter(row => row.tileName === tile),
			};

			// The map: add what was missing, drop saved human boxes the server no longer has.
			const serverIds = new Set(inTile.map(box => box.get('boxId')));
			const onMap = boxesOf(live, nodeUuid, tile);
			const onMapIds = new Set(onMap.map(box => box.get('boxId')));
			bulk(() => {
				onMap
					.filter(
						box =>
							box.get('source') !== 'model' &&
							box.get('saved') &&
							!serverIds.has(box.get('boxId'))
					)
					.forEach(box => live.removeFeature(box));
				restoreBoxes(
					live,
					inTile.filter(box => !onMapIds.has(box.get('boxId')))
				);
			});
			freshTilesRef.current.add(key);
		} catch (error) {
			report(error, 'read-tile', { nodeUuid, tile });
			addToast({
				id: 'annotation-tile-read-failed',
				bg: 'danger',
				title: 'Could not read this tile',
				message: `${error?.message ? `${error.message}. ` : ''}Saving is off for it; open it again to retry.`,
			});
		} finally {
			loadingRef.current.delete(key);
			// Drawing comes back only on the tile that is still open: the user may have
			// moved to another image, which keeps drawing off while it loads.
			if (stale && !staleTilesRef.current.has(key) && tileNameRef.current === tile) {
				syncDrawing();
			}
			refreshDirty();
		}
	};

	const requestList = (nodeUuid, extra = {}) =>
		postNodeAnnotations({
			projectUuid: optionsRef.current.project?.uuid,
			nodeUuid,
			projection: optionsRef.current.serialize.projection,
			includeBackground: true,
			includeEditTargets: true,
			// A save replaces a tile's rows; its earlier rejects are needed to send them again.
			includeRejects: true,
			...extra,
		});

	/**
	 * Fetch the list of an object (inside the boundary) and put it on the map: the
	 * human rows go to the store, the model boxes to the session's cache, and the
	 * tiles the backend holds an empty mark for are checked. A failure toasts and
	 * keeps the object unlisted: Save stays off for it (a save replaces a tile's
	 * rows, and without the list it would drop them) until a tile opens again and
	 * the list is retried.
	 */
	const loadNode = async (node, { readOpenTile = true } = {}) => {
		const live = source();
		if (!node || !live || !optionsRef.current.serialize) return;
		if (loadedRef.current.has(node.uuid) || loadingRef.current.has(node.uuid)) return;
		loadingRef.current.add(node.uuid);
		setLoading(true);
		try {
			const ring = optionsRef.current.boundary?.[0];
			const collection = await requestList(node.uuid, ring ? { aoi: ring } : {});
			const {
				boxes: listed,
				emptyTiles,
				rejected,
				status,
			} = boxesFromFeatures(collection, {
				nodeUuid: node.uuid,
				color: node.color,
				toCoords: optionsRef.current.serialize.toCoords,
			});
			const boxes = placeModelBoxes(listed);

			const humanByTile = {};
			const modelByTile = {};
			emptyTiles.forEach(tile => {
				humanByTile[tile] = [];
			});
			boxes.forEach(box => {
				const tile = box.get('tileName');
				const target = box.get('source') === 'model' ? modelByTile : humanByTile;
				target[tile] = [...(target[tile] ?? []), box];
			});
			const rejectsByTile = {};
			rejected.forEach(row => {
				rejectsByTile[row.tileName] = [...(rejectsByTile[row.tileName] ?? []), row];
			});
			store().replaceNode(node.uuid, humanByTile, status);
			modelBoxesRef.current[node.uuid] = modelByTile;
			storedRejectsRef.current[node.uuid] = rejectsByTile;
			loadedRef.current.add(node.uuid);

			if (!optionsRef.current.perTile) showNode(node.uuid);
			else if (tileNameRef.current) showNode(node.uuid, tileNameRef.current);
			// A tile already open gets its own read before Save turns on.
			if (readOpenTile && tileNameRef.current && sessionNodeRef.current === node.uuid) {
				readTile(tileNameRef.current);
			}
		} catch (error) {
			report(error, 'load-list', { nodeUuid: node.uuid });
			addToast({
				id: 'annotation-list-failed',
				bg: 'danger',
				title: `Could not load the annotations of ${node.name ?? 'this object'}`,
				message: `${error?.message ? `${error.message}. ` : ''}Saving is off for this object until they load; open a tile again to retry.`,
			});
		} finally {
			loadingRef.current.delete(node.uuid);
			setLoading(false);
			refreshDirty();
		}
	};

	/** Keep the open tile only locally: the fallback when no backend call is wired. */
	const saveLocally = (live, nodeUuid, tile) => {
		store().saveTile(nodeUuid, tile, takeSnapshot(live, nodeUuid, tile));
		store().clearRejects(nodeUuid, tile);
	};

	/**
	 * A box drawn or moved shows whatever the box filter says while it is unsaved,
	 * but the save makes it a saved human row, which Detections hides: the box
	 * would vanish the moment Save succeeds. The filter goes back to All instead.
	 */
	const showSavedRows = (live, nodeUuid, tile) => {
		if (store().boxFilter !== ANNOTATION_BOX_FILTER.DETECTIONS) return;
		const hasHuman = boxesOf(live, nodeUuid, tile).some(box => box.get('source') !== 'model');
		if (hasHuman) store().setBoxFilter(ANNOTATION_BOX_FILTER.ALL);
	};

	const saveTile = async () => {
		const live = source();
		const nodeUuid = sessionNodeRef.current;
		const tile = tileNameRef.current;
		if (!live || !nodeUuid || !tile) return false;

		const { serialize: wire, tileAoi: aoiOf } = optionsRef.current;
		if (!canSaveTile(nodeUuid, tile)) return false;
		// A single image is saved as an image; an orthophoto's squares are tiles.
		const perTile = Boolean(optionsRef.current.perTile);
		const unit = perTile ? 'Image' : 'Tile';
		if (!wire) {
			saveLocally(live, nodeUuid, tile);
			showSavedRows(live, nodeUuid, tile);
			// The undo entries point at boxes the save just settled.
			clearHistory();
			refreshDirty();
			addToast({ id: 'annotation-tile-saved', bg: 'success', title: `${unit} saved` });
			return true;
		}

		setSaving(true);
		// Nothing changes on the tile while it is saved: the read-back replaces its
		// boxes, and a box drawn or moved meanwhile would be lost.
		selectBox(null);
		setObjectDrawingActive(false);
		let readBackFailed = false;
		try {
			const annotations = tileSavePayload(
				boxesOf(live, nodeUuid, tile),
				store().rejectsInTile(nodeUuid, tile),
				wire.toRing,
				storedRejectsIn(nodeUuid, tile)
			);
			try {
				await putTileAnnotations({
					projectUuid: optionsRef.current.project?.uuid,
					nodeUuid,
					tileName: tile,
					projection: wire.projection,
					annotations,
				});
			} catch (error) {
				report(error, 'save-tile', { nodeUuid, tile });
				addToast({
					id: 'annotation-tile-save-failed',
					bg: 'danger',
					title: 'Could not save the tile',
					message: error?.message,
				});
				return false;
			}

			// What the backend stored is what the map shows.
			try {
				// Wider than the tile: rows are matched by their centre, kept by `tile_name`.
				const ring = tileReadRing(aoiOf?.(tile));
				const collection = await requestList(nodeUuid, ring ? { aoi: ring } : {});
				adoptTile(nodeUuid, tile, collection, activeNodeRef.current);
			} catch (error) {
				report(error, 'read-back', { nodeUuid, tile });
				// The save went through but what the server holds is unknown: a local
				// snapshot would keep moved model boxes as untouched ones, and the next
				// save would drop their corrections. The tile is read again instead.
				readBackFailed = true;
				freshTilesRef.current.delete(tileKey(nodeUuid, tile));
				staleTilesRef.current.add(tileKey(nodeUuid, tile));
			}
			store().clearRejects(nodeUuid, tile);
			// The read-back replaced the tile's boxes with fresh copies: an undo entry from
			// before the save would bring back a stale box (single image stays on the image).
			clearHistory();
			queryClient.invalidateQueries({ queryKey: ['annotationSummary'] });
			// Collection view's detection results (and the layers built from them) are
			// cached for minutes; after a save they are read again, so leaving annotation
			// shows what the backend now returns for the corrected boxes.
			invalidateCollectionResults(queryClient);
			showSavedRows(live, nodeUuid, tile);
			refreshDirty();
			addToast(
				readBackFailed
					? {
							id: 'annotation-tile-saved',
							bg: 'warning',
							title: `${unit} saved, but it could not be read back`,
							message: perTile
								? 'It is being read again; saving is off until it is read.'
								: 'Open it again before changing it; saving is off until it is read.',
						}
					: { id: 'annotation-tile-saved', bg: 'success', title: `${unit} saved` }
			);
			return true;
		} finally {
			setSaving(false);
			// A stale tile stays without drawing: anything drawn would be replaced when it
			// is read again.
			syncDrawing();
			// A single image stays open after a save: read it again right away rather
			// than wait for it to be reopened (an orthophoto tile is closed by now).
			if (readBackFailed && perTile) readTile(tile);
		}
	};

	/**
	 * The boxes of every object in one tile (an image), after the map was cleared
	 * for it: stored human rows and pristine model boxes. What was erased or
	 * moved there and not saved is gone with the clear. `skipStale` leaves out the
	 * objects whose save of the tile could not be read back: the session still holds
	 * their boxes from before that save.
	 */
	const restoreTile = (tile, { keepRejects = false, skipStale = false } = {}) => {
		const live = source();
		if (!live) return;
		// A per-image rebuild starts the image over; a tile's discard leaves the
		// pending rejects of the other tiles (their erased boxes stay off the map).
		if (!keepRejects) useAnnotationSessionStore.setState({ pendingRejects: {} });
		const present = new Set(live.getFeatures().map(box => box.get('boxId')));
		const models = Object.values(modelBoxesRef.current).flatMap(byTile => byTile[tile] ?? []);
		const stored = [...store().savedInTileAll(tile), ...models].filter(
			box =>
				!present.has(box.get('boxId')) &&
				!(skipStale && staleTilesRef.current.has(tileKey(box.get('nodeUuid'), tile)))
		);
		bulk(() => restoreBoxes(live, stored));
		refreshDirty();
	};

	/**
	 * Whether the open tile holds changes the server does not have. By default every
	 * object's (leaving or cancelling the tile drops them all); `activeOnly` asks for
	 * the session object's alone (what its Save would send). A tile whose save went
	 * through but could not be read back is not counted: the server has those rows.
	 */
	const hasUnsavedChanges = ({ activeOnly = false } = {}) => {
		const live = source();
		const tile = tileNameRef.current;
		if (!live || !tile) return false;
		const { savedByNode, pendingRejects } = store();
		const nodes = activeOnly
			? new Set([sessionNodeRef.current].filter(Boolean))
			: new Set([
					...Object.keys(savedByNode),
					...Object.keys(pendingRejects),
					...live
						.getFeatures()
						.filter(box => box.get('tileName') === tile)
						.map(box => box.get('nodeUuid'))
						.filter(Boolean),
				]);
		for (const nodeUuid of nodes) {
			if (staleTilesRef.current.has(tileKey(nodeUuid, tile))) nodes.delete(nodeUuid);
		}
		return [...nodes].some(nodeUuid =>
			hasChanges(
				live,
				nodeUuid,
				tile,
				store().savedInTile(nodeUuid, tile),
				store().rejectsInTile(nodeUuid, tile)
			)
		);
	};

	/**
	 * Throw away what was done in the open tile since it was last saved: every box
	 * of the tile goes, and what the session holds for it (stored human rows,
	 * pristine model boxes) is drawn again. An object whose save of the tile could not
	 * be read back is left as the map shows it: that is what the server holds, while
	 * the session's copy is from before the save.
	 */
	const discardTile = () => {
		const live = source();
		const tile = tileNameRef.current;
		if (!live || !tile) return;
		const isStale = nodeUuid => staleTilesRef.current.has(tileKey(nodeUuid, tile));
		bulk(() => {
			live.getFeatures()
				.filter(box => box.get('tileName') === tile && !isStale(box.get('nodeUuid')))
				.forEach(box => live.removeFeature(box));
		});
		Object.keys(store().pendingRejects)
			.filter(nodeUuid => !isStale(nodeUuid))
			.forEach(nodeUuid => store().clearRejects(nodeUuid, tile));
		clearHistory();
		restoreTile(tile, { keepRejects: !optionsRef.current.perTile, skipStale: true });
	};

	/**
	 * Back on an object whose tile was saved but not read back: nothing else would read
	 * it again (its list is on the map already), and Save and drawing stay off until it is.
	 */
	const rereadOpenTileIfStale = () => {
		const nodeUuid = sessionNodeRef.current;
		const tile = tileNameRef.current;
		if (nodeUuid && tile && staleTilesRef.current.has(tileKey(nodeUuid, tile))) readTile(tile);
	};

	// The boxes of the object being left stay on the map, saved or not.
	const switchTo = async (node, previousUuid) => {
		const token = ++switchTokenRef.current;
		const isStale = () => token !== switchTokenRef.current;
		setSwitching(true);
		// No drawing while the object changes: a box drawn now would carry the new
		// object but the old grid's tile, and could never be saved.
		setObjectDrawingActive(false);
		try {
			let tiles;
			try {
				tiles = await loadTilesFor(node);
			} catch (error) {
				if (isStale()) return;
				report(error, 'switch-tiles', { nodeUuid: node.uuid });
				addToast({
					id: 'annotation-switch-failed',
					bg: 'danger',
					title: `Could not load the tiles of ${node.name}. Staying on the current object.`,
					message: error?.message,
				});
				dispatch({ type: 'setAnnotationActiveNodeUuid', payload: previousUuid });
				return;
			}
			if (isStale()) return;
			if (tiles && tiles.length === 0) {
				addToast({
					id: 'annotation-switch-no-tiles',
					bg: 'warning',
					title: `${node.name} has no tiles inside the drawn area. Staying on the current object.`,
				});
				dispatch({ type: 'setAnnotationActiveNodeUuid', payload: previousUuid });
				return;
			}

			sessionNodeRef.current = node.uuid;
			// The open tile is the old object's (and its grid's); the switch closes it.
			await loadNode(node, { readOpenTile: false });
			if (isStale()) return;
			// The annotator's callback as it is now, not as it was when the switch began.
			optionsRef.current.onSwitched?.({ node, tiles });
		} finally {
			// A later pick owns the switching state now.
			if (!isStale()) {
				setSwitching(false);
				// Back on while a tile (or image) is still open and current; a switch that
				// closed it keeps it off.
				syncDrawing();
				refreshDirty();
				rereadOpenTileIfStale();
			}
		}
	};

	// The panel picked another object: move the session to it.
	useEffect(() => {
		if (!enabled || !activeNode) return;
		const previousUuid = sessionNodeRef.current;
		if (!previousUuid) {
			sessionNodeRef.current = activeNode.uuid;
			return;
		}
		if (previousUuid === activeNode.uuid) {
			// Back on the session's object while a switch away was loading: that switch
			// is dropped, and the session carries on as it was.
			if (switchingRef.current) {
				switchTokenRef.current += 1;
				setSwitching(false);
				syncDrawing();
				refreshDirty();
				rereadOpenTileIfStale();
			}
			return;
		}
		switchTo(activeNode, previousUuid);
		// switchTo reads the latest store and callbacks at call time.
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [enabled, activeNode?.uuid]);

	return {
		canSave: dirty,
		savesEmpty,
		hasUnsavedBoxes,
		saving,
		loading,
		switching,
		saveTile,
		loadNode,
		restoreTile,
		hasUnsavedChanges,
		canSaveOpenTile: () => {
			const nodeUuid = sessionNodeRef.current;
			const tile = tileNameRef.current;
			return Boolean(nodeUuid && tile && canSaveTile(nodeUuid, tile));
		},
		isOpenTileReading: () => {
			const nodeUuid = sessionNodeRef.current;
			const tile = tileNameRef.current;
			return Boolean(
				nodeUuid &&
					(loadingRef.current.has(nodeUuid) ||
						(tile && loadingRef.current.has(tileKey(nodeUuid, tile))))
			);
		},
		discardTile,
		track,
	};
};
